// pages/EstimationSuccessionPage.jsx
import React, { useState, useEffect } from "react";
import i18next from "i18next";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
const getLocaleCode = () => {
    const language = i18next.resolvedLanguage || i18next.language || "fr";
    if (language.startsWith("de")) return "de-DE";
    if (language.startsWith("en")) return "en-GB";
    return "fr-FR";
};
const formatCurrency = (value) => new Intl.NumberFormat(getLocaleCode(), {
    style: "currency", currency: "EUR", maximumFractionDigits: 2,
}).format(Number(value) || 0);
// Composant pour le choix du pays
function CountrySelector({ selectedCountry, onSelect }) {
    const countries = [
        {
            code: "FR",
            name: i18next.t("estimationSuccession.data.country.FR.name"),
            flag: "🇫🇷",
            regulations: i18next.t("estimationSuccession.data.country.FR.regulations")
        },
        {
            code: "BE",
            name: i18next.t("estimationSuccession.data.country.BE.name"),
            flag: "🇧🇪",
            regulations: i18next.t("estimationSuccession.data.country.BE.regulations")
        },
        {
            code: "CH",
            name: i18next.t("estimationSuccession.data.country.CH.name"),
            flag: "🇨🇭",
            regulations: i18next.t("estimationSuccession.data.country.CH.regulations")
        },
        {
            code: "US",
            name: i18next.t("estimationSuccession.data.country.US.name"),
            flag: "🇺🇸",
            regulations: i18next.t("estimationSuccession.data.country.US.regulations")
        },
        {
            code: "UK",
            name: i18next.t("estimationSuccession.data.country.UK.name"),
            flag: "🇬🇧",
            regulations: i18next.t("estimationSuccession.data.country.UK.regulations")
        }
    ];
    return (<div className="space-y-4">
      <h2 className="text-2xl font-serif mb-6"><Trans i18nKey="estimationSuccession.ui.dansQuelPaysSeSitueLaSuccession"/> </h2>
      <p className="text-gray-600 mb-8"><Trans i18nKey="estimationSuccession.ui.lesReglesDEvaluationDesUvresDArtVarient"/> </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {countries.map((country) => (<button key={country.code} onClick={() => onSelect(country)} className={`
              group text-left border-2 rounded-lg p-6 transition
              ${selectedCountry?.code === country.code
                ? 'border-black bg-gray-50'
                : 'border-gray-200 hover:border-black'}
            `}>
            <div className="flex items-start gap-4">
              <span className="text-4xl">{country.flag}</span>
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-2 group-hover:underline">
                  {country.name}
                </h3>
                <p className="text-sm text-gray-600">
                  {country.regulations}
                </p>
              </div>
            </div>
          </button>))}
      </div>
    </div>);
}
// Composant Belgique
function BelgiqueSuccessionFlow({ artworks, onBack }) {
    const [step, setStep] = useState(1);
    const [selectedRegion, setSelectedRegion] = useState("");
    const [successionData, setSuccessionData] = useState({
        totalValue: "",
        artworkValue: "",
        hasDation: false,
        relationship: "" // ligne directe, collatéraux, etc.
    });
    const regions = [
        { code: "WAL", name: i18next.t("estimationSuccession.data.region.WAL.name"), info: i18next.t("estimationSuccession.data.region.WAL.info") },
        { code: "BXL", name: i18next.t("estimationSuccession.data.region.BXL.name"), info: i18next.t("estimationSuccession.data.region.BXL.info") },
        { code: "FLA", name: i18next.t("estimationSuccession.data.region.FLA.name"), info: i18next.t("estimationSuccession.data.region.FLA.info") }
    ];
    return (<div className="space-y-8">
      <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-black">
        <span>←</span><Trans i18nKey="estimationSuccession.ui.changerDePays"/> </button>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-2"><Trans i18nKey="estimationSuccession.ui.successionEnBelgique"/> </h3>
        <p className="text-sm text-blue-800"><Trans i18nKey="estimationSuccession.ui.valeurVenaleAuJourDuDecesPossibiliteDeDation"/> </p>
      </div>

      {step === 1 && (<div className="bg-white border border-gray-200 rounded-lg p-8">
          <h3 className="text-xl font-serif mb-6"><Trans i18nKey="estimationSuccession.ui.selectionnezLaRegionDuDefunt"/> </h3>
          <p className="text-gray-600 mb-6"><Trans i18nKey="estimationSuccession.ui.lesDroitsDeSuccessionVarientSelonLaRegionDe"/> </p>

          <div className="space-y-3">
            {regions.map((region) => (<button key={region.code} onClick={() => {
                    setSelectedRegion(region.code);
                    setStep(2);
                }} className="w-full text-left border-2 border-gray-200 rounded-lg p-4 hover:border-black transition">
                <h4 className="font-semibold mb-1">{region.name}</h4>
                <p className="text-sm text-gray-600">{region.info}</p>
              </button>))}
          </div>
        </div>)}

      {step === 2 && (<div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <h3 className="text-xl font-serif mb-6"><Trans i18nKey="estimationSuccession.ui.evaluationDeLUvreDArt"/> </h3>

            <div className="space-y-6">
              <div>
                <label className="block font-medium mb-2"><Trans i18nKey="estimationSuccession.ui.valeurVenaleDeLUvreAuJourDuDeces"/> </label>
                <input type="number" value={successionData.artworkValue} onChange={(e) => setSuccessionData({
                ...successionData,
                artworkValue: e.target.value
            })} placeholder={i18next.t("estimationSuccession.ui.ex50000")} className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"/>
                <p className="text-sm text-gray-500 mt-1"><Trans i18nKey="estimationSuccession.ui.estimationParCommissairePriseurOuExpertRecommandee"/> </p>
              </div>

              <div>
                <label className="block font-medium mb-2"><Trans i18nKey="estimationSuccession.ui.valeurTotaleDeLaSuccession"/> </label>
                <input type="number" value={successionData.totalValue} onChange={(e) => setSuccessionData({
                ...successionData,
                totalValue: e.target.value
            })} placeholder={i18next.t("estimationSuccession.ui.ex500000")} className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"/>
              </div>

              <div>
                <label className="block font-medium mb-2"><Trans i18nKey="estimationSuccession.ui.lienDeParenteAvecLeDefunt"/> </label>
                <select value={successionData.relationship} onChange={(e) => setSuccessionData({
                ...successionData,
                relationship: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black">
                  <option value=""><Trans i18nKey="estimationSuccession.ui.selectionner"/></option>
                  <option value="conjoint"><Trans i18nKey="estimationSuccession.ui.conjointCohabitantLegal"/></option>
                  <option value="ligne_directe"><Trans i18nKey="estimationSuccession.ui.ligneDirecteEnfantsParents"/></option>
                  <option value="collatéraux"><Trans i18nKey="estimationSuccession.ui.freresSUrsOnclesTantes"/></option>
                  <option value="autres"><Trans i18nKey="estimationSuccession.ui.autresHeritiers"/></option>
                </select>
              </div>
            </div>
          </div>

          {/* Option dation */}
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-8">
            <h4 className="font-semibold text-green-900 mb-3"><Trans i18nKey="estimationSuccession.ui.optionDationEnPaiement"/> </h4>
            <p className="text-sm text-green-800 mb-4"><Trans i18nKey="estimationSuccession.ui.vousPouvezPayerLesDroitsDeSuccessionEnCedant"/> </p>
            
            {selectedRegion === "FLA" && (<div className="bg-white border border-green-300 rounded p-4 mb-4">
                <p className="text-sm font-medium text-green-900 mb-2"><Trans i18nKey="estimationSuccession.ui.avantageFlandreReforme2023"/> </p>
                <ul className="text-sm text-green-800 space-y-1">
                  <li><Trans i18nKey="estimationSuccession.ui.evaluationA"/> <strong><Trans i18nKey="estimationSuccession.ui.120DeLaValeurVenale"/></strong></li>
                  <li><Trans i18nKey="estimationSuccession.ui.elargissementAuxBiensCulturelsBijouxManuscritsObjetsArcheologiqu"/></li>
                  <li><Trans i18nKey="estimationSuccession.ui.remboursementDuSurplusSiValeurDroitsDus"/></li>
                </ul>
              </div>)}

            <label className="flex items-start gap-3">
              <input type="checkbox" checked={successionData.hasDation} onChange={(e) => setSuccessionData({
                ...successionData,
                hasDation: e.target.checked
            })} className="mt-1"/>
              <div>
                <span className="font-medium"><Trans i18nKey="estimationSuccession.ui.envisagerLaDationEnPaiement"/></span>
                <p className="text-xs text-gray-600"><Trans i18nKey="estimationSuccession.ui.necessiteAccordDeLEtatEtExpertiseFraisA"/> </p>
              </div>
            </label>
          </div>

          {/* Points clés Belgique */}
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <h4 className="font-semibold mb-4"><Trans i18nKey="estimationSuccession.ui.pointsClesBelgique"/></h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex gap-2">
                <span className="text-gray-400">•</span>
                <span>
                  <strong><Trans i18nKey="estimationSuccession.ui.valeurVenale"/></strong><Trans i18nKey="estimationSuccession.ui.auJourDuDecesMarcheDesBiensDOccasion"/> </span>
              </li>
              <li className="flex gap-2">
                <span className="text-gray-400">•</span>
                <span>
                  <strong><Trans i18nKey="estimationSuccession.ui.expertiseRecommandee"/></strong><Trans i18nKey="estimationSuccession.ui.parCommissairePriseurPourUvresImportantes"/> </span>
              </li>
              <li className="flex gap-2">
                <span className="text-gray-400">•</span>
                <span>
                  <strong><Trans i18nKey="estimationSuccession.ui.tauxVariables"/></strong><Trans i18nKey="estimationSuccession.ui.selonRegionDegreDeParenteEtMontant"/> </span>
              </li>
              <li className="flex gap-2">
                <span className="text-gray-400">•</span>
                <span>
                  <strong><Trans i18nKey="estimationSuccession.ui.dationPossible"/></strong><Trans i18nKey="estimationSuccession.ui.cessionDUvresAuLieuDuPaiementEnNumeraire"/> </span>
              </li>
              <li className="flex gap-2">
                <span className="text-gray-400">•</span>
                <span>
                  <strong><Trans i18nKey="estimationSuccession.ui.controleFiscal"/></strong><Trans i18nKey="estimationSuccession.ui.lAdministrationPeutDemanderUneExpertiseDeControle"/> </span>
              </li>
            </ul>
          </div>

          <div className="flex gap-3">
            <button onClick={() => setStep(1)} className="px-6 py-3 border-2 border-gray-300 rounded hover:border-black"><Trans i18nKey="estimationSuccession.ui.retour"/> </button>
            <Link to="/fiche/estimation" className="px-6 py-3 bg-black text-white rounded hover:bg-gray-800"><Trans i18nKey="estimationSuccession.ui.demanderUneExpertise"/> </Link>
          </div>
        </div>)}
    </div>);
}
// Composant Suisse
function SuisseSuccessionFlow({ artworks, onBack }) {
    const [step, setStep] = useState(1);
    const [selectedCanton, setSelectedCanton] = useState("");
    const [successionData, setSuccessionData] = useState({
        artworkValue: "",
        relationship: "",
        hasDation: false
    });
    const cantons = [
        { code: "GE", name: i18next.t("estimationSuccession.data.canton.GE.name"), exoneration: i18next.t("estimationSuccession.data.canton.GE.exemption"), dation: true },
        { code: "VD", name: i18next.t("estimationSuccession.data.canton.VD.name"), exoneration: i18next.t("estimationSuccession.data.canton.VD.exemption"), dation: true },
        { code: "ZH", name: i18next.t("estimationSuccession.data.canton.ZH.name"), exoneration: i18next.t("estimationSuccession.data.canton.ZH.exemption"), dation: false },
        { code: "BE", name: i18next.t("estimationSuccession.data.canton.BE.name"), exoneration: i18next.t("estimationSuccession.data.canton.BE.exemption"), dation: false },
        { code: "VS", name: i18next.t("estimationSuccession.data.canton.VS.name"), exoneration: i18next.t("estimationSuccession.data.canton.VS.exemption"), dation: false },
        { code: "TI", name: i18next.t("estimationSuccession.data.canton.TI.name"), exoneration: i18next.t("estimationSuccession.data.canton.TI.exemption"), dation: false },
        { code: "SZ", name: i18next.t("estimationSuccession.data.canton.SZ.name"), exoneration: i18next.t("estimationSuccession.data.canton.SZ.exemption"), dation: false },
        { code: "OW", name: i18next.t("estimationSuccession.data.canton.OW.name"), exoneration: i18next.t("estimationSuccession.data.canton.OW.exemption"), dation: false }
    ];
    const selectedCantonData = cantons.find(c => c.code === selectedCanton);
    return (<div className="space-y-8">
      <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-black">
        <span>←</span><Trans i18nKey="estimationSuccession.ui.changerDePays"/> </button>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-2"><Trans i18nKey="estimationSuccession.ui.successionEnSuisse"/> </h3>
        <p className="text-sm text-blue-800"><Trans i18nKey="estimationSuccession.ui.26RegimesCantonauxDifferentsValeurVenaleAuJourDu"/> </p>
      </div>

      {step === 1 && (<div className="bg-white border border-gray-200 rounded-lg p-8">
          <h3 className="text-xl font-serif mb-6"><Trans i18nKey="estimationSuccession.ui.selectionnezLeCantonDuDernierDomicileDuDefunt"/> </h3>
          <p className="text-gray-600 mb-6"><Trans i18nKey="estimationSuccession.ui.lImpotSurLesSuccessionsEstPreleveParLe"/> </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {cantons.map((canton) => (<button key={canton.code} onClick={() => {
                    setSelectedCanton(canton.code);
                    setStep(2);
                }} className="text-left border-2 border-gray-200 rounded-lg p-4 hover:border-black transition">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold mb-1">{canton.name}</h4>
                    <p className="text-xs text-gray-600">{canton.exoneration}</p>
                  </div>
                  {canton.dation && (<span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      Dation
                    </span>)}
                </div>
              </button>))}
          </div>
        </div>)}

      {step === 2 && selectedCantonData && (<div className="space-y-6">
          {/* Info canton sélectionné */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold"><Trans i18nKey="estimationSuccession.ui.cantonDe"/> {selectedCantonData.name}</h3>
                <p className="text-sm text-gray-600 mt-1">{selectedCantonData.exoneration}</p>
              </div>
              <button onClick={() => setStep(1)} className="text-sm text-gray-500 hover:text-black underline">
                Changer
              </button>
            </div>

            {(selectedCanton === "SZ" || selectedCanton === "OW") && (<div className="bg-green-50 border border-green-200 rounded p-4">
                <p className="text-sm font-medium text-green-900"><Trans i18nKey="estimationSuccession.ui.aucunImpotSurLesSuccessionsDansCeCanton"/> </p>
              </div>)}
          </div>

          {selectedCanton !== "SZ" && selectedCanton !== "OW" && (<>
              {/* Formulaire */}
              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <h3 className="text-xl font-serif mb-6"><Trans i18nKey="estimationSuccession.ui.informationsSurLUvre"/> </h3>

                <div className="space-y-6">
                  <div>
                    <label className="block font-medium mb-2"><Trans i18nKey="estimationSuccession.ui.valeurVenaleDeLUvreChf"/> </label>
                    <input type="number" value={successionData.artworkValue} onChange={(e) => setSuccessionData({
                    ...successionData,
                    artworkValue: e.target.value
                })} placeholder={i18next.t("estimationSuccession.ui.ex50000")} className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"/>
                    <p className="text-sm text-gray-500 mt-1"><Trans i18nKey="estimationSuccession.ui.evaluationALaValeurDuMarcheAuJourDu"/> </p>
                  </div>

                  <div>
                    <label className="block font-medium mb-2"><Trans i18nKey="estimationSuccession.ui.lienAvecLeDefunt"/> </label>
                    <select value={successionData.relationship} onChange={(e) => setSuccessionData({
                    ...successionData,
                    relationship: e.target.value
                })} className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black">
                      <option value=""><Trans i18nKey="estimationSuccession.ui.selectionner"/></option>
                      <option value="conjoint"><Trans i18nKey="estimationSuccession.ui.conjointPartenaireEnregistre"/></option>
                      <option value="enfants"><Trans i18nKey="estimationSuccession.ui.enfantsPetitsEnfants"/></option>
                      <option value="parents">Parents</option>
                      <option value="freres_soeurs"><Trans i18nKey="estimationSuccession.ui.freresEtSUrs"/></option>
                      <option value="autres"><Trans i18nKey="estimationSuccession.ui.autresHeritiers"/></option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Dation si disponible */}
              {selectedCantonData.dation && (<div className="bg-green-50 border-2 border-green-200 rounded-lg p-8">
                  <h4 className="font-semibold text-green-900 mb-3"><Trans i18nKey="estimationSuccession.ui.dationEnPaiementDisponible"/> </h4>
                  <p className="text-sm text-green-800 mb-4"><Trans i18nKey="estimationSuccession.ui.leCantonDe"/> {selectedCantonData.name}<Trans i18nKey="estimationSuccession.ui.permetDePayerLImpotSurLesSuccessionsEn"/> </p>

                  {selectedCanton === "VD" && (<div className="bg-white border border-green-300 rounded p-4 mb-4">
                      <p className="text-sm font-medium mb-2"><Trans i18nKey="estimationSuccession.ui.cantonDeVaudLdpi2005"/></p>
                      <ul className="text-sm text-green-800 space-y-1">
                        <li><Trans i18nKey="estimationSuccession.ui.serviceDesAffairesCulturellesSeracEvalueLInteret"/></li>
                        <li><Trans i18nKey="estimationSuccession.ui.enrichissementDesCollectionsDuMuseeCantonal"/></li>
                        <li><Trans i18nKey="estimationSuccession.ui.procedureDAgrementRequise"/></li>
                      </ul>
                    </div>)}

                  <label className="flex items-start gap-3">
                    <input type="checkbox" checked={successionData.hasDation} onChange={(e) => setSuccessionData({
                        ...successionData,
                        hasDation: e.target.checked
                    })} className="mt-1"/>
                    <span className="text-sm"><Trans i18nKey="estimationSuccession.ui.envisagerLaDationEnPaiementPourCetteUvre"/> </span>
                  </label>
                </div>)}

              {/* Spécificités cantonales */}
              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <h4 className="font-semibold mb-4"><Trans i18nKey="estimationSuccession.ui.specificitesDuCanton"/></h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex gap-2">
                    <span className="text-gray-400">•</span>
                    <span>
                      <strong><Trans i18nKey="estimationSuccession.ui.tauxVariables"/></strong><Trans i18nKey="estimationSuccession.ui.26SystemesDifferents0A50"/> </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-gray-400">•</span>
                    <span>
                      <strong><Trans i18nKey="estimationSuccession.ui.impotCommunal"/></strong><Trans i18nKey="estimationSuccession.ui.peutSAjouterALImpotCantonal"/> </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-gray-400">•</span>
                    <span>
                      <strong><Trans i18nKey="estimationSuccession.ui.delaiDePaiement"/></strong><Trans i18nKey="estimationSuccession.ui.generalement30JoursApresNotification"/> </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-gray-400">•</span>
                    <span>
                      <strong><Trans i18nKey="estimationSuccession.ui.valeurVenale"/></strong><Trans i18nKey="estimationSuccession.ui.evaluationAuJourDuDecesMarcheSecondaire"/> </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-gray-400">•</span>
                    <span>
                      <strong><Trans i18nKey="estimationSuccession.ui.pasDImpotFederal"/></strong><Trans i18nKey="estimationSuccession.ui.seulsLesCantonsPreleventLImpot"/> </span>
                  </li>
                </ul>
              </div>
            </>)}

          <div className="flex gap-3">
            <button onClick={() => setStep(1)} className="px-6 py-3 border-2 border-gray-300 rounded hover:border-black"><Trans i18nKey="estimationSuccession.ui.changerDeCanton"/> </button>
            <Link to="/fiche/estimation" className="px-6 py-3 bg-black text-white rounded hover:bg-gray-800"><Trans i18nKey="estimationSuccession.ui.demanderUneExpertise"/> </Link>
          </div>
        </div>)}
    </div>);
}
// Composant spécifique France
function FranceSuccessionFlow({ artworks, onBack }) {
    const [step, setStep] = useState(1);
    useState(null); // selectedArtwork — reserved for future use
    const [successionData, setSuccessionData] = useState({
        totalSuccessionValue: "",
        hasPublicSale: false,
        publicSaleDate: "",
        publicSalePrice: "",
        hasInventory: false,
        inventoryDate: "",
        inventoryValue: "",
        hasInsurance: false,
        insuranceValue: "",
        insuranceDate: "",
        useForfait: true
    });
    const calculateForfait = () => {
        const total = parseFloat(successionData.totalSuccessionValue) || 0;
        return (total * 0.05).toFixed(2);
    };
    const getRecommendation = () => {
        const forfait = parseFloat(calculateForfait());
        const inventoryValue = parseFloat(successionData.inventoryValue) || 0;
        if (inventoryValue > 0 && inventoryValue < forfait) {
            return {
                method: i18next.t("estimationSuccession.recommendation.inventory"),
                reason: i18next.t("estimationSuccession.recommendation.savings", { amount: formatCurrency(forfait - inventoryValue) }),
                action: i18next.t("estimationSuccession.ui.faireEtablirUnInventaireParUnCommissairePriseurOu")
            };
        }
        else if (forfait < 1000) {
            return {
                method: i18next.t("estimationSuccession.recommendation.flatRate"),
                reason: i18next.t("estimationSuccession.ui.montantForfaitaireFaibleEtSimple"),
                action: i18next.t("estimationSuccession.ui.utiliserLeForfaitMobilierStandard")
            };
        }
        else {
            return {
                method: i18next.t("estimationSuccession.recommendation.toEvaluate"),
                reason: i18next.t("estimationSuccession.ui.comparerInventaireVsForfaitSelonValeurReelle"),
                action: i18next.t("estimationSuccession.ui.consulterUnNotaireOuExpertComptable")
            };
        }
    };
    return (<div className="space-y-8">
      {/* Breadcrumb */}
      <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-black">
        <span>←</span><Trans i18nKey="estimationSuccession.ui.changerDePays"/> </button>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-2"><Trans i18nKey="estimationSuccession.ui.successionEnFranceArticle764Cgi"/> </h3>
        <p className="text-sm text-blue-800"><Trans i18nKey="estimationSuccession.ui.hierarchieLegale1VentePublique2Ans2Inventaire"/> </p>
      </div>

      {/* Étape 1 : Données de base */}
      {step === 1 && (<div className="bg-white border border-gray-200 rounded-lg p-8">
          <h3 className="text-xl font-serif mb-6"><Trans i18nKey="estimationSuccession.ui.informationsSurLaSuccession"/> </h3>

          <div className="space-y-6">
            <div>
              <label className="block font-medium mb-2"><Trans i18nKey="estimationSuccession.ui.valeurTotaleDeLaSuccessionAvantDeductionDesDettes"/> </label>
              <input type="number" value={successionData.totalSuccessionValue} onChange={(e) => setSuccessionData({
                ...successionData,
                totalSuccessionValue: e.target.value
            })} placeholder={i18next.t("estimationSuccession.ui.ex500000")} className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"/>
              <p className="text-sm text-gray-500 mt-1"><Trans i18nKey="estimationSuccession.ui.inclutImmobilierPlacementsAutresBiensMobiliers"/> </p>
            </div>

            {successionData.totalSuccessionValue && (<div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                <p className="text-sm font-medium"><Trans i18nKey="estimationSuccession.ui.forfaitMobilier5"/> {calculateForfait()} €
                </p>
                <p className="text-xs text-gray-600 mt-1"><Trans i18nKey="estimationSuccession.ui.siVousUtilisezLeForfaitLesMeublesUvresSeront"/> </p>
              </div>)}

            <div className="pt-6 border-t">
              <h4 className="font-semibold mb-4"><Trans i18nKey="estimationSuccession.ui.concernantLUvreAEstimer"/> </h4>

              {/* Vente publique */}
              <div className="space-y-4">
                <label className="flex items-start gap-3">
                  <input type="checkbox" checked={successionData.hasPublicSale} onChange={(e) => setSuccessionData({
                ...successionData,
                hasPublicSale: e.target.checked
            })} className="mt-1"/>
                  <div className="flex-1">
                    <span className="font-medium"><Trans i18nKey="estimationSuccession.ui.ventePubliqueDansLes2Ans"/></span>
                    <p className="text-sm text-gray-600"><Trans i18nKey="estimationSuccession.ui.prixDeVenteObligatoireSiVenteAuxEncheresApres"/> </p>
                  </div>
                </label>

                {successionData.hasPublicSale && (<div className="ml-6 space-y-3">
                    <input type="date" value={successionData.publicSaleDate} onChange={(e) => setSuccessionData({
                    ...successionData,
                    publicSaleDate: e.target.value
                })} className="w-full px-4 py-2 border border-gray-300 rounded" placeholder={i18next.t("estimationSuccession.ui.dateDeLaVente")}/>
                    <input type="number" value={successionData.publicSalePrice} onChange={(e) => setSuccessionData({
                    ...successionData,
                    publicSalePrice: e.target.value
                })} className="w-full px-4 py-2 border border-gray-300 rounded" placeholder={i18next.t("estimationSuccession.ui.prixDeVente")}/>
                  </div>)}
              </div>

              {/* Inventaire */}
              {!successionData.hasPublicSale && (<div className="space-y-4 pt-4">
                  <label className="flex items-start gap-3">
                    <input type="checkbox" checked={successionData.hasInventory} onChange={(e) => setSuccessionData({
                    ...successionData,
                    hasInventory: e.target.checked
                })} className="mt-1"/>
                    <div className="flex-1">
                      <span className="font-medium"><Trans i18nKey="estimationSuccession.ui.inventaireDansLes5Ans"/></span>
                      <p className="text-sm text-gray-600"><Trans i18nKey="estimationSuccession.ui.parNotaireCommissairePriseurOuExpert"/> </p>
                    </div>
                  </label>

                  {successionData.hasInventory && (<div className="ml-6 space-y-3">
                      <input type="date" value={successionData.inventoryDate} onChange={(e) => setSuccessionData({
                        ...successionData,
                        inventoryDate: e.target.value
                    })} className="w-full px-4 py-2 border border-gray-300 rounded" placeholder={i18next.t("estimationSuccession.ui.dateDeLInventaire")}/>
                      <input type="number" value={successionData.inventoryValue} onChange={(e) => setSuccessionData({
                        ...successionData,
                        inventoryValue: e.target.value
                    })} className="w-full px-4 py-2 border border-gray-300 rounded" placeholder={i18next.t("estimationSuccession.ui.valeurEstimee")}/>
                    </div>)}
                </div>)}

              {/* Assurance */}
              {!successionData.hasPublicSale && !successionData.hasInventory && (<div className="space-y-4 pt-4">
                  <label className="flex items-start gap-3">
                    <input type="checkbox" checked={successionData.hasInsurance} onChange={(e) => setSuccessionData({
                    ...successionData,
                    hasInsurance: e.target.checked
                })} className="mt-1"/>
                    <div className="flex-1">
                      <span className="font-medium"><Trans i18nKey="estimationSuccession.ui.contratDAssuranceDeMoinsDe10Ans"/></span>
                      <p className="text-sm text-gray-600"><Trans i18nKey="estimationSuccession.ui.volIncendieOuDommage"/> </p>
                    </div>
                  </label>

                  {successionData.hasInsurance && (<div className="ml-6 space-y-3">
                      <input type="date" value={successionData.insuranceDate} onChange={(e) => setSuccessionData({
                        ...successionData,
                        insuranceDate: e.target.value
                    })} className="w-full px-4 py-2 border border-gray-300 rounded" placeholder={i18next.t("estimationSuccession.ui.dateDuContrat")}/>
                      <input type="number" value={successionData.insuranceValue} onChange={(e) => setSuccessionData({
                        ...successionData,
                        insuranceValue: e.target.value
                    })} className="w-full px-4 py-2 border border-gray-300 rounded" placeholder={i18next.t("estimationSuccession.ui.valeurAssuree")}/>
                    </div>)}
                </div>)}
            </div>

            <button onClick={() => setStep(2)} disabled={!successionData.totalSuccessionValue} className="w-full px-6 py-3 bg-black text-white rounded hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed"><Trans i18nKey="estimationSuccession.ui.voirLaRecommandation"/> </button>
          </div>
        </div>)}

      {/* Étape 2 : Recommandation */}
      {step === 2 && (<div className="space-y-6">
          {/* Résumé */}
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <h3 className="text-xl font-serif mb-6"><Trans i18nKey="estimationSuccession.ui.recapitulatif"/></h3>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600"><Trans i18nKey="estimationSuccession.ui.valeurTotaleSuccession"/></span>
                <span className="font-semibold">
                  {formatCurrency(successionData.totalSuccessionValue)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600"><Trans i18nKey="estimationSuccession.ui.forfaitMobilier547ea06"/></span>
                <span className="font-semibold">{formatCurrency(calculateForfait())}</span>
              </div>
              {successionData.inventoryValue && (<div className="flex justify-between">
                  <span className="text-gray-600"><Trans i18nKey="estimationSuccession.ui.valeurInventaire"/></span>
                  <span className="font-semibold">
                    {formatCurrency(successionData.inventoryValue)}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Recommandation */}
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-8">
            <div className="flex items-start gap-4">
              <span className="text-4xl">✓</span>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-green-900 mb-2"><Trans i18nKey="estimationSuccession.ui.recommandation"/> {getRecommendation().method}
                </h3>
                <p className="text-green-800 mb-4">
                  {getRecommendation().reason}
                </p>
                <p className="text-sm font-medium text-green-900">
                  → {getRecommendation().action}
                </p>
              </div>
            </div>
          </div>

          {/* Points d'attention */}
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <h4 className="font-semibold mb-4"><Trans i18nKey="estimationSuccession.ui.pointsDAttention"/></h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex gap-2">
                <span className="text-gray-400">•</span>
                <span><Trans i18nKey="estimationSuccession.ui.lAdministrationFiscalePeutContesterEtDemanderUnePreuve"/> </span>
              </li>
              <li className="flex gap-2">
                <span className="text-gray-400">•</span>
                <span><Trans i18nKey="estimationSuccession.ui.distinctionEntreMeublesMeublantsTableauxDOrnementEtObjets"/> </span>
              </li>
              <li className="flex gap-2">
                <span className="text-gray-400">•</span>
                <span><Trans i18nKey="estimationSuccession.ui.delaisVentePublique2AnsInventaire5AnsAssurance"/> </span>
              </li>
              <li className="flex gap-2">
                <span className="text-gray-400">•</span>
                <span><Trans i18nKey="estimationSuccession.ui.conserverTousLesJustificatifsFacturesExpertisesContrats"/> </span>
              </li>
            </ul>
          </div>

          {/* CTA Expert */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8">
            <h4 className="font-semibold mb-3"><Trans i18nKey="estimationSuccession.ui.besoinDUnAccompagnementProfessionnel"/> </h4>
            <p className="text-sm text-gray-700 mb-4"><Trans i18nKey="estimationSuccession.ui.unExpertComptableSpecialiseEnFiscaliteDesSuccessionsOu"/> </p>
            <div className="flex gap-3">
              <Link to="/fiche/estimation" className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800"><Trans i18nKey="estimationSuccession.ui.demanderUneExpertise"/> </Link>
              <button onClick={() => setStep(1)} className="px-4 py-2 border-2 border-gray-300 rounded hover:border-black"><Trans i18nKey="estimationSuccession.ui.modifierLesDonnees"/> </button>
            </div>
          </div>
        </div>)}
    </div>);
}
// Composant principal
export default function EstimationSuccessionPage() {
    useTranslation();
    const { user } = useAuth();
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [artworks, setArtworks] = useState([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (user?.id) {
            loadArtworks();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);
    const loadArtworks = async () => {
        try {
            const { data, error } = await supabase
                .from("artworks")
                .select("id, title, artist, value")
                .eq("client_id", user.id);
            if (error)
                throw error;
            setArtworks(data || []);
        }
        catch (error) {
            console.error("Erreur chargement œuvres:", error);
        }
    };
    return (<div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <h1 className="text-4xl font-serif mb-2"><Trans i18nKey="estimationSuccession.ui.estimationPourSuccession"/> </h1>
          <p className="text-gray-600"><Trans i18nKey="estimationSuccession.ui.outilDAideALaDeclarationFiscaleSelonVotre"/> </p>
        </div>
      </header>

      {/* Contenu */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        {!selectedCountry ? (<CountrySelector selectedCountry={selectedCountry} onSelect={setSelectedCountry}/>) : selectedCountry.code === "FR" ? (<FranceSuccessionFlow artworks={artworks} onBack={() => setSelectedCountry(null)}/>) : selectedCountry.code === "BE" ? (<BelgiqueSuccessionFlow artworks={artworks} onBack={() => setSelectedCountry(null)}/>) : selectedCountry.code === "CH" ? (<SuisseSuccessionFlow artworks={artworks} onBack={() => setSelectedCountry(null)}/>) : (<div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <span className="text-6xl mb-4 block">{selectedCountry.flag}</span>
            <h3 className="text-xl font-semibold mb-2">
              {selectedCountry.name}
            </h3>
            <p className="text-gray-600 mb-6"><Trans i18nKey="estimationSuccession.ui.leModuleDEstimationSuccessionPourCePaysEst"/> </p>
            <button onClick={() => setSelectedCountry(null)} className="px-6 py-2 border-2 border-black rounded hover:bg-black hover:text-white"><Trans i18nKey="estimationSuccession.ui.choisirUnAutrePays"/> </button>
          </div>)}

        {/* Ressources */}
        <div className="mt-12 bg-white border border-gray-200 rounded-lg p-8">
          <h3 className="text-lg font-serif mb-4"><Trans i18nKey="estimationSuccession.ui.ressourcesUtiles"/></h3>
          <div className="space-y-2 text-sm">
            <a href="https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000042912987" target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:underline"><Trans i18nKey="estimationSuccession.ui.article764DuCodeGeneralDesImpotsFrance"/> </a>
            <a href="https://bofip.impots.gouv.fr/bofip/1389-PGP.html" target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:underline"><Trans i18nKey="estimationSuccession.ui.bofipEvaluationDesBiensMeublesFrance"/> </a>
            <Link to="/fiche/succession" className="block text-blue-600 hover:underline"><Trans i18nKey="estimationSuccession.ui.fichePratiqueSuccessionArtkiv"/> </Link>
          </div>
        </div>
      </main>
    </div>);
}
